import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ProcureXCommercialThread from "../components/ProcureXCommercialThread";
import SamgrahaLogo, { ProcureXIcon } from "../components/SamgrahaLogo";
import {
  ChevronLeft, Plus, Minus, BarChart3, Shield, Layers, FileText,
  Cpu, Zap, TrendingUp, Users, Eye, Target, Activity, Package,
  ArrowRight, Database, Globe, Search, CheckCircle2, AlertTriangle,
  ClipboardCheck, Truck, Award, Scale
} from "lucide-react";

/* ───── CountUp ───── */
function CountUp({ end, prefix = "", suffix = "", duration = 1600, decimals = 0 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const t0 = performance.now();
        const tick = (t) => {
          const p = Math.min(1, (t - t0) / duration);
          setVal(end * (1 - Math.pow(1 - p, 3)));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.3 });
    io.observe(el);
    return () => io.disconnect();
  }, [end, duration]);
  const d = decimals ? val.toFixed(decimals) : Math.round(val).toLocaleString("en-IN");
  return <span ref={ref}>{prefix}{d}{suffix}</span>;
}

/* ═══════ DATA ═══════ */
const PILLARS = [
  { n: 1, name: "Category Management" },
  { n: 2, name: "Supplier Management" },
  { n: 3, name: "Sourcing Execution" },
  { n: 4, name: "Contract Lifecycle" },
  { n: 5, name: "Execution & Closeout" },
];

const MODULES = [
  { pillar: 1, slug: "category-strategy", name: "Category Taxonomy & Strategy", Icon: BarChart3, body: "UNSPSC + custom taxonomy for capital project spend. Kraljic Matrix auto-classification. Spend cube with drill-down. Should-cost modeling. Demand forecasting linked to project schedules." },
  { pillar: 1, slug: "market-intelligence", name: "Market Intelligence & Benchmarking", Icon: TrendingUp, body: "Commodity price tracking (steel, copper, cement, diesel). Benchmarking database from Capexara deployments. Risk indices per category. Quarterly category review automation." },
  { pillar: 2, slug: "supplier-onboarding", name: "Supplier Onboarding & KYS", Icon: Users, body: "Configurable onboarding workflow. Financial health screening (MCA, D&B). ESG scoring (Scope 1/2/3). Sanctions screening (OFAC, EU, UN). Statutory compliance (GST, BOCW, ESIC, PF)." },
  { pillar: 2, slug: "supplier-360", name: "Supplier 360° Performance", Icon: Target, body: "Multi-dimensional scorecard: quality, delivery, price, responsiveness, HSE, innovation. Weighted scoring by category. Supplier tiering. Peer benchmarking. Development plans with milestones." },
  { pillar: 2, slug: "supplier-risk", name: "Supplier Risk Monitoring", Icon: AlertTriangle, body: "Continuous financial health monitoring. Geopolitical risk alerts. Concentration risk analytics. Early warning system with escalation workflows." },
  { pillar: 3, slug: "sourcing-cockpit", name: "Sourcing Cockpit (9 Event Types)", Icon: Activity, body: "RFI, RFP, RFQ, Forward eAuction, Reverse eAuction, Sealed Bid, Two-Envelope, BAFO, Framework Award. Event calendar, Q&A boards, technical + commercial scoring, award recommendation." },
  { pillar: 3, slug: "bid-analytics", name: "Bid Analytics & Negotiation Support", Icon: Search, body: "Bid comparison matrix. Anomaly detection. Savings calculation vs baseline/budget/should-cost. Negotiation playbook suggestions. BATNA analysis." },
  { pillar: 4, slug: "contract-authoring", name: "Contract Authoring", Icon: FileText, body: "FIDIC clause library (Red/Yellow/Silver/Gold/Emerald/Green Books). NEC clause library (NEC3, NEC4). Indian standard clauses. AI risk flagging. Collaborative redlining. Digital signature (DSC + Aadhaar eSign + e-signature platforms)." },
  { pillar: 4, slug: "contract-repository", name: "Contract Repository & Obligations", Icon: Database, body: "Searchable contract repository with clause-level indexing. AI obligation extraction. Obligation tracking with alerts. Contract renewal management. Performance bond tracking." },
  { pillar: 4, slug: "claims", name: "Claims & Change Orders", Icon: Scale, body: "Variation order management. GenAI claim drafting with clause citations. Counter-claim analysis. Delay analysis integrated with Panorix schedule. Claim valuation with should-cost models. Dispute resolution tracking." },
  { pillar: 5, slug: "po-management", name: "PO Management & Expediting", Icon: Truck, body: "PO issuance with multi-level approval. Amendment management. Expediting dashboard. Shipment tracking with IoT. Goods Receipt + Quality Inspection. 3-way invoice matching (PO + GR + Invoice)." },
  { pillar: 5, slug: "warranty-closeout", name: "Warranty & Closeout", Icon: Shield, body: "Warranty period tracking per asset. Defect liability management. Performance guarantee monitoring. Retention release workflow. Supplier final evaluation. Project closeout audit." },
];

const FAQS = [
  { q: "How is ProcureX different from enterprise procurement suites?", a: "enterprise procurement suites were built for indirect spend (office supplies, software, travel). ProcureX is built native for capital projects — LSTK contracts, FIDIC/NEC clauses, multi-year warranty periods, complex claims, capex-specific categories. Most enterprises run ProcureX for capital projects and keep procurement suites for operational/indirect spend." },
  { q: "Can ProcureX replace our existing ERP procurement modules or procurement suites?", a: "For capital projects procurement, yes. For broader indirect spend, typically ProcureX coexists with ERP procurement via bidirectional data integration (enterprise ERP BAPIs/OData or procurement suites APIs). Some enterprises deploy only for strategic/capex categories." },
  { q: "How does the FIDIC clause library work?", a: "Pre-loaded, structured clause library covering FIDIC Red, Yellow, Silver, Gold, Emerald, and Green books. Each clause annotated with risk commentary, recommended modifications, and precedent case references. Customizable with your organization's standard modifications." },
  { q: "Does ProcureX support Indian statutory compliance?", a: "Yes. GST-compliant (e-invoicing, ITC tracking, GSP integration), BOCW-compliant, integrated with MCA21, ICEGATE, DGFT. For PSU clients, CVC/CBC procurement policies and CPPP integration are supported." },
  { q: "How does the AI drafting of claims work?", a: "Cognara integration uses RAG over your contract corpus + precedent database. Given a situation, it drafts a claim letter with clause citations, precedent references, quantum calculation, evidence linkages, and risk-weighted recommendations. Human attorney reviews and finalizes — AI accelerates drafting 5-10×." },
  { q: "What about data residency and security?", a: "Three deployment models: shared Azure cloud, dedicated Azure with India data residency (Mumbai/Hyderabad), or on-premise. Self-hosted LLM deployment (Llama/Mistral) supported. Contract content can be air-gapped from external LLM services." },
  { q: "How does ProcureX handle multi-currency and forex?", a: "Native multi-currency with real-time exchange rates, hedging integration, LC management. Supports all major currencies plus INR-specific workflows (customs valuation, transfer pricing). Forex P&L tracking across the contract lifecycle." },
  { q: "What's the typical deployment timeline?", a: "10-14 weeks for 8+ modules. Phase 1 (weeks 1-4): config, taxonomy, supplier onboarding, ERP integration. Phase 2 (weeks 5-10): contract library, clause customization, training. Phase 3 (weeks 11-14): pilot sourcing events, go-live." },
  { q: "What's the pricing model?", a: "Three models: (1) Subscription per user per module (₹36-90 Lakh/year for 80 users, 7 modules), (2) Project-based (₹1.5-4 Cr one-time), (3) Enterprise agreement (₹6-30 Cr/year). All include hosting, support, and 2 major releases per year." },
  { q: "How does ProcureX integrate with Panorix?", a: "Natively. Procurement data flows into Panorix's Cost Control, Contracts & Claims, and Risk modules. Panorix's schedule slip detection triggers ProcureX's claim drafting. Both share authentication, RBAC, and cognitive layer (Cognara)." },
];

/* ═══════ PAGE EXPORT ═══════ */
export default function PlatformSamgraha() {
  return (
    <>
      <Section1Hero />
      <Section2Problem />
      <Section3Modules />
      <Section4Cockpit />
      <Section5ContractAI />
      <Section6Integrations />
      <Section7Industries />
      <Section8Outcomes />
      <Section9Faq />
      <Section10Cta />
    </>
  );
}

/* ═══════ S1 HERO — grantha-hero-grid pattern ═══════ */
function Section1Hero() {
  return (
    <section className="grantha-hero" data-testid="sg-hero">
      <div className="container grantha-hero-grid">
        <div className="grantha-hero-copy">
          <Link to="/platforms" className="detail-back" data-testid="sg-back">
            <ChevronLeft size={14} /> All platforms
          </Link>
          <div className="grantha-crumb mono">Platforms / Enterprise Digital / PROCUREX</div>
          <div className="eyebrow" style={{ marginTop: 10, display: "inline-flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <span>Procurement · Supply Chain · Contracts Lifecycle</span>
          </div>
          <h1 className="grantha-h1" data-testid="sg-h1">
            <span className="grantha-indo sg-logo-lockup" data-testid="sg-logo">
              <span className="rc-mark-wrap"><ProcureXIcon size={88} /></span>
              <span className="rc-wordmark">
                Pro<span className="rc-c">c</span>ureX
              </span>
            </span>
            <span className="grantha-h1-en">The source-to-settle spine for <em>capital projects.</em></span>
          </h1>
          <p className="grantha-sub" data-testid="sg-hero-sub">
            On a ₹5,000 Cr refinery, procurement and contracts touch ₹3,800 Cr of spend across 1,200 POs, 47 LSTK contracts, and 340 suppliers. ProcureX is the AI-native platform that orchestrates every rupee — from category strategy through final warranty closeout — on one cognitive canvas.
          </p>
          <div className="grantha-ctas">
            <a href="https://procurex.capexara.com/" target="_blank" rel="noopener noreferrer" className="btn btn-primary" data-testid="sg-cta-access">Access ProcureX <span className="arrow">→</span></a>
            <Link to="/contact?p=procurex" className="btn btn-ghost" data-testid="sg-cta-demo">Request Demo <span className="arrow">→</span></Link>
            <a href="#sg-cockpit" className="btn btn-ghost">See the Sourcing Cockpit</a>
          </div>
          <div className="grantha-trust mono">
            FIDIC & NEC clause libraries · procurement-platform migration-ready · GST + BOCW + SEBI compliant · Azure + on-premise
          </div>
        </div>
        <div className="grantha-hero-stage" data-testid="sg-hero-viz">
          <ProcureXCommercialThread />
        </div>
      </div>
    </section>
  );
}

/* Source-to-settle journey visualization */
function SourceToSettleViz() {
  const [aiIdx, setAiIdx] = useState(0);
  const insights = [
    "Bid pattern anomaly — Vendor XYZ priced 23% above market",
    "Supplier ABC health score dropped to B- (-2 notches)",
    "Clause 14.3 flagged: LD cap at risk",
    "Claim #47 draft: 14 clause citations, ready for review",
    "Steel prices forecast +8% in Q3 — update category strategy",
  ];
  useEffect(() => {
    const iv = setInterval(() => setAiIdx(i => (i + 1) % insights.length), 4000);
    return () => clearInterval(iv);
  }, [insights.length]);

  const stages = [
    { x: 50, label: "CATEGORY", sub: "Kraljic + spend" },
    { x: 160, label: "SUPPLIER", sub: "KYS + ESG screen" },
    { x: 280, label: "SOURCING", sub: "9 event types" },
    { x: 400, label: "CONTRACT", sub: "FIDIC/NEC CLM" },
    { x: 510, label: "CLOSEOUT", sub: "PO → Warranty" },
  ];

  return (
    <svg viewBox="0 0 560 560" className="grantha-vault" aria-label="Source-to-settle procurement journey visualization">
      <defs>
        <linearGradient id="sgGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#1E4E8C" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#2E70AD" stopOpacity="0.06" />
        </linearGradient>
      </defs>

      {/* Background circle */}
      <circle cx="280" cy="280" r="220" fill="#1E4E8C" fillOpacity="0.03" />
      <circle cx="280" cy="280" r="180" fill="none" stroke="#1E4E8C" strokeOpacity="0.1" strokeDasharray="3 8" style={{ transformOrigin: "280px 280px", animation: "orbitSpin 60s linear infinite" }} />

      {/* Central procurement hexagon */}
      <g style={{ transformOrigin: "280px 280px", animation: "vaultFloat 6s ease-in-out infinite" }}>
        <polygon points="280,180 360,230 360,330 280,380 200,330 200,230" fill="url(#sgGrad)" stroke="#1E4E8C" strokeWidth="1.5" strokeOpacity="0.7" />
        <polygon points="280,180 360,230 280,280 200,230" fill="#1E4E8C" fillOpacity="0.12" stroke="#1E4E8C" strokeWidth="0.8" strokeOpacity="0.4" />
        <polygon points="280,280 360,230 360,330 280,380" fill="#2E70AD" fillOpacity="0.06" stroke="#2E70AD" strokeWidth="0.8" strokeOpacity="0.3" />
        {/* Inner labels */}
        <text x="280" y="270" textAnchor="middle" fontSize="10" fill="#1E4E8C" fontFamily="var(--f-mono)" fontWeight="700" letterSpacing="2">iPCLM</text>
        <text x="280" y="290" textAnchor="middle" fontSize="7" fill="var(--text-3)" fontFamily="var(--f-mono)">12 MODULES</text>
        {/* Mini Kraljic inside */}
        <rect x="250" y="305" width="24" height="24" fill="none" stroke="#1E4E8C" strokeOpacity="0.4" strokeWidth="0.5" />
        <rect x="250" y="305" width="12" height="12" fill="#1E4E8C" fillOpacity="0.2" />
        <rect x="262" y="317" width="12" height="12" fill="#2E70AD" fillOpacity="0.15" />
        <rect x="280" y="305" width="24" height="24" fill="none" stroke="#1E4E8C" strokeOpacity="0.4" strokeWidth="0.5" />
        <rect x="280" y="305" width="12" height="12" fill="#F26B1F" fillOpacity="0.15" />
      </g>

      {/* 5 stage nodes orbiting */}
      {stages.map((s, i) => {
        const angle = (i * 72 - 90) * (Math.PI / 180);
        const nx = 280 + 170 * Math.cos(angle);
        const ny = 280 + 170 * Math.sin(angle);
        return (
          <g key={i} style={{ animation: `vaultNode 4s ease-in-out ${i * 0.15}s infinite` }}>
            <line x1="280" y1="280" x2={nx} y2={ny} stroke="#2E70AD" strokeOpacity="0.25" strokeWidth="1" strokeDasharray="2 4" />
            <circle cx={nx} cy={ny} r="28" fill="var(--bg-elev)" stroke="#1E4E8C" strokeWidth="1.5" />
            <text x={nx} y={ny - 4} textAnchor="middle" fontSize="7" fontWeight="700" fill="#1E4E8C" fontFamily="var(--f-mono)">{s.label}</text>
            <text x={nx} y={ny + 8} textAnchor="middle" fontSize="5.5" fill="var(--text-3)" fontFamily="var(--f-mono)">{s.sub}</text>
            {/* Data packet */}
            <circle r="2.5" fill="#F26B1F" style={{ offsetPath: `path('M 280 280 L ${nx} ${ny}')`, animation: `pipeFlow ${3 + i * 0.3}s linear infinite`, animationDelay: `${i * 0.5}s` }} />
          </g>
        );
      })}

      {/* AI insight bar */}
      <rect x="90" y="485" width="380" height="28" rx="4" fill="var(--bg-elev)" stroke="#1E4E8C" strokeOpacity="0.2" strokeWidth="0.5" />
      <text x="100" y="503" fontSize="7" fill="#F26B1F" fontFamily="var(--f-mono)" fontWeight="700">AI</text>
      <text x="118" y="503" fontSize="6.5" fill="var(--text-2)" fontFamily="var(--f-mono)">{insights[aiIdx]}</text>

      {/* Bottom label */}
      <text x="280" y="535" textAnchor="middle" fontSize="9" fill="#F26B1F" fontFamily="var(--f-mono)" letterSpacing="2" fontWeight="700">₹3,800 Cr · 1,200 POs · 340 SUPPLIERS</text>
    </svg>
  );
}

/* ═══════ S2 PROBLEM — grantha-problem pattern ═══════ */
function Section2Problem() {
  const stats = [
    { n: 3800, prefix: "₹", suffix: " Cr", label: "procurement spend on a ₹5,000 Cr refinery project" },
    { n: 14, suffix: "%", label: "average value leakage on a complex capex buy (price, scope change, spec drift)" },
    { n: 14, suffix: " months", label: "typical cycle from category strategy to final supplier contract" },
    { n: 42, suffix: "%", label: "of capital project claims originate in contract ambiguity or missed clauses" },
  ];
  return (
    <section className="grantha-problem" data-testid="sg-problem">
      <div className="container">
        <div className="eyebrow" style={{ color: "var(--ember)" }}>The capital procurement problem</div>
        <h2 className="grantha-problem-h">
          <em>Your ERP handles transactions. Your SRM handles suppliers. Your CLM handles contracts. Nothing handles the whole journey.</em>
        </h2>
        <div className="grantha-problem-grid">
          {stats.map((s, i) => (
            <div className="grantha-problem-tile" key={i} data-testid={`sg-stat-${i}`}>
              <div className="grantha-problem-num">
                <CountUp end={s.n} prefix={s.prefix || ""} suffix={s.suffix || ""} />
              </div>
              <div className="grantha-problem-label">{s.label}</div>
            </div>
          ))}
        </div>
        <p className="grantha-problem-quote">
          A purchase order is the first contract. <em>A contract is the last purchase order.</em>
        </p>
      </div>
    </section>
  );
}

/* ═══════ S3 MODULES — grantha-arch layer-tab pattern ═══════ */
function Section3Modules() {
  const [activePillar, setActivePillar] = useState(1);
  return (
    <section className="grantha-arch" data-testid="sg-modules">
      <div className="container">
        <div className="eyebrow">Platform architecture</div>
        <h2 className="grantha-arch-h">Twelve modules. <em>One source-to-settle spine.</em></h2>
        <p className="grantha-arch-sub">
          ProcureX organises into five logical pillars — from category intelligence through supplier management, sourcing execution, contract lifecycle, and execution closeout. Click any pillar to explore.
        </p>
        <div className="grantha-layers" data-testid="sg-pillars">
          {PILLARS.map((P) => (
            <button key={P.n} type="button" className={"grantha-layer " + (activePillar === P.n ? "is-active" : "")} onMouseEnter={() => setActivePillar(P.n)} onClick={() => setActivePillar(P.n)} data-testid={`sg-pillar-${P.n}`}>
              <span className="grantha-layer-n mono">PILLAR · 0{P.n}</span>
              <span className="grantha-layer-name">{P.name}</span>
              <span className="grantha-layer-count mono">{MODULES.filter(m => m.pillar === P.n).length}</span>
            </button>
          ))}
        </div>
        <div className="grantha-modules" data-testid="sg-mod-grid">
          {MODULES.filter(m => m.pillar === activePillar).map((m) => (
            <div key={m.slug} className="grantha-module" data-testid={`sg-mod-${m.slug}`}>
              <div className="grantha-module-icon"><m.Icon size={22} /></div>
              <div className="grantha-module-body">
                <div className="grantha-module-name">{m.name}</div>
                <div className="grantha-module-desc">{m.body}</div>
              </div>
            </div>
          ))}
        </div>
        <p style={{ fontFamily: "var(--f-serif)", fontStyle: "italic", fontSize: 18, color: "var(--text-2)", textAlign: "center", marginTop: 40 }}>
          From the first RFI to the final warranty release — twelve modules, one cognitive spine, zero spreadsheets.
        </p>
      </div>
    </section>
  );
}

/* ═══════ S4 SOURCING COCKPIT — grantha-viewer-grid pattern ═══════ */
function Section4Cockpit() {
  const features = [
    { n: "01", t: "Nine event types, one interface", b: "RFI, RFP, RFQ, Forward Auction, Reverse eAuction, Sealed Bid, Two-Envelope, BAFO, Framework Award. Same cockpit, different rulesets." },
    { n: "02", t: "Real-time bid visibility", b: "See bids as they land. Supplier cursors, activity streams, Q&A threads, all live. Full audit trail." },
    { n: "03", t: "AI-powered bid analytics", b: "Anomaly detection, benchmark comparison, should-cost analysis, BATNA suggestions. A reasoning engine for negotiations." },
    { n: "04", t: "Technical + commercial scoring", b: "Configurable weighted scoring, multi-evaluator workflows, bias checks, audit-ready justifications." },
    { n: "05", t: "Auto-generated award recommendation", b: "AI drafts award-committee-ready notes with comparison tables, risk analysis, and procurement policy compliance." },
  ];
  return (
    <section className="grantha-viewer" id="sg-cockpit" data-testid="sg-cockpit">
      <div className="container grantha-viewer-grid">
        <div className="grantha-viewer-visual">
          <div className="grantha-viewer-frame">
            <div className="grantha-viewer-bar">
              <span className="mono">ProcureX · RFQ-2847 · Structural Steel Frame</span>
              <span className="mono" style={{ color: "#F26B1F" }}>LIVE · 2d 14h</span>
            </div>
            <svg viewBox="0 0 420 280" className="grantha-viewer-svg">
              <rect width="420" height="280" fill="var(--bg-sunk)" />
              {/* Event timeline sidebar */}
              <rect x="0" y="0" width="80" height="280" fill="var(--bg-elev)" stroke="var(--border)" strokeWidth="0.5" />
              <text x="10" y="20" fontSize="6" fill="var(--text-3)" fontFamily="var(--f-mono)">EVENT TIMELINE</text>
              {["Created", "Invited (8)", "Q&A Closed", "Bidding ▶", "Tech Eval", "Comm Eval", "Award"].map((step, i) => (
                <g key={i}>
                  <circle cx="18" cy={40 + i * 32} r="4" fill={i < 3 ? "#10B981" : i === 3 ? "#1E4E8C" : "none"} stroke={i < 4 ? "#10B981" : "var(--border)"} strokeWidth="1" />
                  <text x="28" y={43 + i * 32} fontSize="6" fill={i === 3 ? "var(--text)" : "var(--text-3)"} fontFamily="var(--f-mono)" fontWeight={i === 3 ? "600" : "400"}>{step}</text>
                </g>
              ))}

              {/* Bid Rankings */}
              <text x="95" y="20" fontSize="7" fill="var(--text-3)" fontFamily="var(--f-mono)">BID RANKINGS</text>
              {[
                { n: "#1", name: "Alpha", price: "₹412.3 Cr", delta: "-2.3%" },
                { n: "#2", name: "Beta", price: "₹418.7 Cr", delta: "-0.8%" },
                { n: "#3", name: "Gamma", price: "₹424.1 Cr", delta: "+0.4%" },
                { n: "#4", name: "Delta", price: "₹431.2 Cr", delta: "+2.0%" },
                { n: "#5", name: "Epsilon", price: "₹438.6 Cr", delta: "+3.8%" },
                { n: "#6", name: "Zeta", price: "₹445.2 Cr", delta: "+5.4%" },
              ].map((bid, i) => (
                <g key={i}>
                  <rect x="90" y={28 + i * 22} width="135" height="18" rx="2" fill="none" stroke="var(--border)" strokeWidth="0.3" />
                  <text x="96" y={40 + i * 22} fontSize="6" fill={i === 0 ? "var(--steel)" : "var(--text-2)"} fontFamily="var(--f-mono)" fontWeight={i === 0 ? "700" : "400"}>{bid.n} {bid.name}</text>
                  <text x="160" y={40 + i * 22} fontSize="6" fill="var(--text)" fontFamily="var(--f-mono)">{bid.price}</text>
                  <text x="210" y={40 + i * 22} fontSize="5" fill={bid.delta.startsWith("-") ? "#10B981" : "#F26B1F"} fontFamily="var(--f-mono)">{bid.delta}</text>
                </g>
              ))}

              {/* Radar chart area */}
              <text x="240" y="20" fontSize="7" fill="var(--text-3)" fontFamily="var(--f-mono)">BID ANALYTICS</text>
              <polygon points="340,80 370,110 360,150 320,150 310,110" fill="#1E4E8C" fillOpacity="0.15" stroke="#1E4E8C" strokeWidth="0.8" />
              <polygon points="340,85 365,108 356,143 324,143 315,108" fill="#2E70AD" fillOpacity="0.08" stroke="#2E70AD" strokeWidth="0.5" strokeDasharray="2 2" />
              {["Price", "Tech", "Time", "Track", "ESG"].map((lbl, i) => {
                const a = (i * 72 - 90) * Math.PI / 180;
                const lx = 340 + 45 * Math.cos(a);
                const ly = 115 + 45 * Math.sin(a);
                return <text key={i} x={lx} y={ly} textAnchor="middle" fontSize="5" fill="var(--text-3)" fontFamily="var(--f-mono)">{lbl}</text>;
              })}

              {/* AI insight */}
              <rect x="235" y="170" width="175" height="40" rx="4" fill="rgba(242,107,31,0.06)" stroke="rgba(242,107,31,0.2)" strokeWidth="0.5" />
              <text x="242" y="184" fontSize="5.5" fill="#F26B1F" fontFamily="var(--f-mono)" fontWeight="700">AI INSIGHT</text>
              <text x="242" y="196" fontSize="5" fill="var(--text-2)" fontFamily="var(--f-sans)">Alpha bid 2.3% below market. Delivery</text>
              <text x="242" y="205" fontSize="5" fill="var(--text-2)" fontFamily="var(--f-sans)">risk: ±3wk variance. Rec: buffer.</text>

              {/* Activity stream */}
              <text x="240" y="225" fontSize="6" fill="var(--text-3)" fontFamily="var(--f-mono)">ACTIVITY</text>
              {["Beta revised bid (2m ago)", "Gamma viewed docs (12m)", "AI flagged Delta spec (1h)", "Alpha submitted (3h)"].map((act, i) => (
                <text key={i} x="242" y={238 + i * 10} fontSize="5" fill="var(--text-3)" fontFamily="var(--f-mono)">· {act}</text>
              ))}
            </svg>
            <div className="grantha-viewer-meta mono">
              <div>RFQ-2847 · 8 suppliers · 6 bids · ₹412-445 Cr range</div>
              <div>Reverse eAuction · Last refresh: 8s ago</div>
            </div>
          </div>
        </div>
        <div className="grantha-viewer-copy">
          <div className="eyebrow">The sourcing command canvas</div>
          <h2 className="grantha-viewer-h"><em>Every sourcing event,</em> orchestrated on one canvas.</h2>
          <div className="grantha-features">
            {features.map((f) => (
              <div className="grantha-feature" key={f.n}>
                <span className="grantha-feature-n mono">{f.n}</span>
                <div>
                  <div className="grantha-feature-t">{f.t}</div>
                  <div className="grantha-feature-b">{f.b}</div>
                </div>
              </div>
            ))}
          </div>
          <blockquote className="grantha-pull">
            A reverse auction is theater. <em>ProcureX makes it cinema.</em>
          </blockquote>
        </div>
      </div>
    </section>
  );
}

/* ═══════ S5 CONTRACT INTELLIGENCE — grantha-wf dark band ═══════ */
function Section5ContractAI() {
  const [clauseIdx, setClauseIdx] = useState(0);
  const clauses = [
    { num: "14.3", color: "#F26B1F", text: "LD cap at 10% — below category median (15%)" },
    { num: "17.8", color: "#1E4E8C", text: "Missing Force Majeure carve-out for natural disasters" },
    { num: "22.4", color: "#10B981", text: "Strong payment terms — Net 30 with interest on delay" },
    { num: "14↔17", color: "#F26B1F", text: "Conflict: Clause 14 caps vs Clause 17 uncapped" },
  ];
  useEffect(() => {
    const iv = setInterval(() => setClauseIdx(i => (i + 1) % clauses.length), 6000);
    return () => clearInterval(iv);
  }, [clauses.length]);

  return (
    <section className="grantha-wf" data-testid="sg-contract-ai">
      <div className="container grantha-wf-grid">
        <div>
          <div className="eyebrow" style={{ color: "var(--steel-200)" }}>The contract intelligence layer</div>
          <h2 className="grantha-wf-h">AI that reads contracts like <em>a 30-year legal counsel.</em></h2>
          <p className="grantha-wf-body">
            Built on Capexara's proprietary clause corpus — 50,000+ annotated clauses from FIDIC, NEC, bespoke Indian capital-project contracts, and arbitration precedents. It reads, writes, and reasons about contracts with the fluency of senior legal counsel.
          </p>
          <ul className="grantha-wf-list">
            <li>Clause-level risk detection — every clause scored on liability, delay, payment, termination</li>
            <li>Missing clause detection — benchmarks draft against category norms</li>
            <li>Clause conflict detection — identifies contradictions within the same contract</li>
            <li>Obligation extraction — reads contracts, extracts every commitment and deadline</li>
            <li>Claim drafting — clause citations, precedent references, quantum models</li>
            <li>Counter-claim analysis — merits assessment, weakness identification, response drafting</li>
          </ul>
        </div>
        <div className="grantha-wf-diagram" data-testid="sg-contract-diagram">
          <svg viewBox="0 0 360 420" style={{ width: "100%", maxWidth: 360, margin: "0 auto", display: "block" }}>
            {/* Contract document */}
            <rect x="60" y="20" width="180" height="260" rx="6" fill="rgba(30,78,140,0.06)" stroke="#1E4E8C" strokeOpacity="0.3" strokeWidth="1" />
            <text x="80" y="45" fontSize="8" fill="#1E4E8C" fontFamily="var(--f-mono)" fontWeight="700">CONTRACT DRAFT</text>
            {/* Clause lines */}
            {Array.from({ length: 12 }, (_, i) => (
              <rect key={i} x="75" y={60 + i * 18} width={100 + (i % 3) * 20} height="6" rx="1" fill={
                (clauseIdx === 0 && i === 3) ? "rgba(242,107,31,0.25)" :
                (clauseIdx === 1 && i === 6) ? "rgba(30,78,140,0.25)" :
                (clauseIdx === 2 && i === 9) ? "rgba(16,185,129,0.25)" :
                (clauseIdx === 3 && (i === 3 || i === 6)) ? "rgba(242,107,31,0.2)" :
                "rgba(30,78,140,0.08)"
              } />
            ))}

            {/* AI annotation */}
            <g>
              <rect x="60" y="295" width="240" height="36" rx="5" fill="rgba(10,14,26,0.8)" stroke={clauses[clauseIdx].color} strokeOpacity="0.4" strokeWidth="0.8" />
              <text x="72" y="310" fontSize="7" fill={clauses[clauseIdx].color} fontFamily="var(--f-mono)" fontWeight="700">Clause {clauses[clauseIdx].num}</text>
              <text x="72" y="323" fontSize="6.5" fill="rgba(243,244,246,0.7)" fontFamily="var(--f-mono)">{clauses[clauseIdx].text}</text>
            </g>

            {/* Derived deliverables */}
            <text x="60" y="355" fontSize="7" fill="rgba(243,244,246,0.5)" fontFamily="var(--f-mono)" letterSpacing="1">AI OUTPUTS</text>
            {[
              "Obligations Tracker (27 extracted)",
              "Risk Heatmap (4 clauses high-risk)",
              "Claim Template (pre-drafted)",
              "Full Audit Trail (logged)",
            ].map((item, i) => (
              <g key={i}>
                <rect x="60" y={365 + i * 14} width="200" height="11" rx="2" fill="rgba(30,78,140,0.08)" stroke="#1E4E8C" strokeOpacity="0.15" strokeWidth="0.5" />
                <text x="66" y={373 + i * 14} fontSize="6" fill="rgba(243,244,246,0.6)" fontFamily="var(--f-mono)">{item}</text>
              </g>
            ))}
          </svg>
        </div>
      </div>
    </section>
  );
}

/* ═══════ S6 INTEGRATIONS — grantha-integ pattern ═══════ */
function Section6Integrations() {
  const groups = [
    { head: "ERP Systems", items: ["enterprise ERP (PM, MM, FI/CO, procurement suites)", "enterprise ERP suites", "enterprise ERP", "Infor ERP · Tally ERP"] },
    { head: "Procurement Platforms", items: ["procurement platforms (migration + coexist)", "spend-management platforms (migration + coexist)", "Jaggaer · GEP SMART", "Ivalua"] },
    { head: "Banks & Financial", items: ["SWIFT messaging (MT/MX)", "Bank APIs (SBI, ICICI, HDFC, Citi)", "LC management · Trade finance", "GST Suvidha Provider (GSP)"] },
    { head: "Statutory & Compliance", items: ["GSTN for e-invoicing + returns", "MCA21 for company data", "BOCW / ESIC / PF for labor", "ICEGATE · DGFT for trade"] },
  ];
  return (
    <section className="grantha-integ" data-testid="sg-integrations">
      <div className="container">
        <div className="eyebrow">Built for your stack</div>
        <h2 className="grantha-integ-h">Integrates with ERPs, SRMs, banks, and <em>statutory systems.</em></h2>
        <div className="grantha-integ-grid" style={{ gridTemplateColumns: "repeat(4, 1fr)" }}>
          {groups.map((g, i) => (
            <div className="grantha-integ-col" key={i}>
              <div className="grantha-integ-head mono">{g.head}</div>
              <ul className="grantha-integ-list">
                {g.items.map((it) => <li key={it}>{it}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════ S7 INDUSTRIES — grantha-inds pattern ═══════ */
function Section7Industries() {
  const useCases = [
    { title: "Greenfield EPC Procurement", desc: "Full source-to-settle for ₹5,000 Cr+ projects. 1,200+ POs, 47 LSTK contracts, 340 suppliers." },
    { title: "Brownfield Revamp Procurement", desc: "Time-critical, spec-tight procurement for plant turnarounds. Emphasis on expediting and LC management." },
    { title: "Framework Agreement Management", desc: "Multi-year framework agreements across categories. Volume-commit + price-discovery hybrids." },
    { title: "OEM & Technology Licensing", desc: "Complex technology licensing contracts. Long-tail obligations and royalty management." },
    { title: "Claims & Dispute Management", desc: "₹100-500 Cr active disputes. Claim drafting, quantum modeling, arbitration support." },
    { title: "Owner's Procurement Oversight", desc: "Lightweight deployment for audit dashboards, PO approval workflows, compliance oversight." },
  ];
  return (
    <section className="grantha-inds" data-testid="sg-industries">
      <div className="container">
        <div className="eyebrow">Industries &amp; use cases</div>
        <h2 className="grantha-inds-h">Deployed across <em>capital-project procurement</em> globally.</h2>
        <div className="grantha-chips" style={{ marginBottom: 40 }}>
          {["Oil, Gas & Energy", "Power & Utilities", "Metals, Mining & Minerals", "Chemicals", "Infrastructure", "Marine", "Manufacturing"].map((c) => (
            <Link to="/industries" className="grantha-chip" key={c}>{c}</Link>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16 }}>
          {useCases.map((uc, i) => (
            <div key={i} style={{ padding: "18px", background: "var(--bg-elev)", border: "1px solid var(--border)", borderLeft: "3px solid var(--steel)", borderRadius: "var(--radius-sm)" }} data-testid={`sg-usecase-${i}`}>
              <div style={{ fontFamily: "var(--f-display)", fontSize: 15, fontWeight: 600, color: "var(--text)", marginBottom: 4 }}>{uc.title}</div>
              <div style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.45 }}>{uc.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════ S8 OUTCOMES — grantha-out pattern ═══════ */
function Section8Outcomes() {
  const cards = [
    { big: 4.8, suffix: "%", head: "average savings on procurement spend via better sourcing", body: "On a ₹3,800 Cr buy: ₹182 Cr savings.", decimals: 1 },
    { big: 62, suffix: "%", head: "reduction in sourcing event cycle time", body: "From 8-12 weeks to 3-4 weeks per event." },
    { big: 78, suffix: "%", head: "reduction in claims cycle time", body: "From 180+ days to 40-60 days per claim." },
  ];
  return (
    <section className="grantha-out" data-testid="sg-outcomes">
      <div className="container">
        <div className="eyebrow">Measured impact</div>
        <h2 className="grantha-out-h">What enterprises measure after deploying <em>ProcureX.</em></h2>
        <div className="grantha-out-grid">
          {cards.map((c, i) => (
            <div className="grantha-out-card" key={i} data-testid={`sg-out-${i}`}>
              <div className="grantha-out-num">
                <CountUp end={c.big} suffix={c.suffix || ""} decimals={c.decimals || 0} />
              </div>
              <div className="grantha-out-head">{c.head}</div>
              <div className="grantha-out-body">{c.body}</div>
            </div>
          ))}
        </div>
        <Link to="/resources" className="grantha-out-cta">Download the ProcureX ROI whitepaper <span className="arrow">→</span></Link>
      </div>
    </section>
  );
}

/* ═══════ S9 FAQ — grantha-faq pattern ═══════ */
function Section9Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section className="grantha-faq" data-testid="sg-faq">
      <div className="container">
        <div className="eyebrow">Frequently asked</div>
        <h2 className="grantha-faq-h">Everything a <em>CPO or Head of Contracts</em> wants to know.</h2>
        <div className="grantha-faq-list">
          {FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className={"grantha-faq-item " + (isOpen ? "is-open" : "")} data-testid={`sg-faq-${i}`}>
                <button type="button" onClick={() => setOpen(isOpen ? -1 : i)} className="grantha-faq-q" aria-expanded={isOpen}>
                  <span>{f.q}</span>
                  {isOpen ? <Minus size={18} /> : <Plus size={18} />}
                </button>
                {isOpen && <div className="grantha-faq-a" data-testid={`sg-faq-answer-${i}`}>{f.a}</div>}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ═══════ S10 CTA — grantha-cta pattern ═══════ */
function Section10Cta() {
  return (
    <section className="grantha-cta" data-testid="sg-cta">
      <div className="container grantha-cta-inner">
        <div className="eyebrow" style={{ color: "#F26B1F" }}>See ProcureX in action</div>
        <h2 className="grantha-cta-h">Bring us your hardest sourcing event or active claim — <em>we'll show you how ProcureX handles it, live.</em></h2>
        <p className="grantha-cta-sub">
          Our "Live Sourcing Session" is a 60-minute working demo where we load an anonymized version of your actual RFQ (under NDA), configure the sourcing cockpit, run through bid analytics, or walk through an active claim with AI-drafted response letters.
        </p>
        <div className="grantha-cta-row">
          <Link to="/contact?p=procurex" className="btn btn-primary grantha-cta-btn" data-testid="sg-cta-session">Book a Live Sourcing Session <span className="arrow">→</span></Link>
          <Link to="/resources" className="btn btn-ghost grantha-cta-ghost">Download datasheet (PDF)</Link>
        </div>
      </div>
    </section>
  );
}
